import { ContentfulLivePreview } from "@contentful/live-preview";
import type { Entry } from "contentful";

import Logger from "~/services/logger/logger";
import Editorials from "~/types/editorials";

import localeConfig from "../../locale.config";
import { isActiveEntry } from "./editorial-utils";

const contentfulUtils = {
  localesMap: localeConfig.contentfulLocales,
  /**
   * Maps a Next.js/Vercel locale to the corresponding Contentful locale.
   *
   * Contentful does not allow custom locales, so we have some shared rules.
   * - All ME countries are grouped under AE.
   *
   * @param locale - The locale for the current request
   */
  mapLocale(locale: string | undefined) {
    if (!locale || !locale.includes("-")) {
      Logger.instance.error(`Invalid locale ${locale} found in request, will use default en-US`);
      return "en-US";
    }

    if (locale in this.localesMap) {
      return this.localesMap[locale as keyof typeof this.localesMap];
    }

    Logger.instance.warn(`Locale ${locale} is not mapped, will use it as-is.`);
    const [language, country] = locale.split("-");

    // Contentful only allows uppercase countries.
    // Even though this is a fallback, the country is assumed to be uppercase.
    // E.g. a path of /it-it/ will become it-IT
    return `${language}-${country.toUpperCase()}`;
  },

  /**
   * Check if string is wrapped in "%" symbols
   *
   * @param {string} highlightString - string to check
   *
   * @returns {boolean}
   */
  isHighlightText(highlightString: string) {
    return highlightString?.includes("%%");
  },

  /**
   * Check if entry comes from contentful
   *
   * @param {any} entry - entry to check
   *
   * @returns {boolean}
   */
  isContentful<T>(entry: any): entry is Entry<T> {
    return entry && entry.sys;
  },

  /**
   * Merge products with editorials. Used in product listing pages
   *
   * @param {(T | Entry<Editorials.GridElement>)[]} products - products that can be from Eva or Algolia
   * @param { Entry<Editorials.GridElement>[]} components - contentful editorial components
   *
   * @returns {(T | Entry<Editorials.GridElement>)[] | undefined}
   */
  mergeProductsWithEditorials<T>(
    products?: (T | Entry<Editorials.GridElement>)[],
    components?: Entry<Editorials.GridElement>[]
  ): (T | Entry<Editorials.GridElement>)[] | undefined {
    const productsWithEditorialElementTemp: (T | Entry<Editorials.GridElement>)[] | undefined = products?.slice();

    const editorialElementsWithoutDraftContent = components?.filter(isActiveEntry);

    const editorialElementsWithoutDuplicates = editorialElementsWithoutDraftContent?.reduce(
      (accumulator: Entry<Editorials.GridElement>[], current) => {
        if (!accumulator.find((item) => item.fields.gridIndex === current.fields.gridIndex)) {
          accumulator.push(current);
        }
        return accumulator;
      },
      []
    );

    const editorialElementsSortedByGridIndex = editorialElementsWithoutDuplicates?.sort(
      (number1, number2) => number1.fields.gridIndex - number2.fields.gridIndex
    );

    editorialElementsSortedByGridIndex?.forEach((editorialElement, idx) => {
      //editorial elements with an index higher than the current number of products will not be inserted
      if (
        productsWithEditorialElementTemp &&
        editorialElement.fields.gridIndex < productsWithEditorialElementTemp?.length - idx
      ) {
        productsWithEditorialElementTemp?.splice(editorialElement.fields.gridIndex, 0, editorialElement);
      }
    });
    return productsWithEditorialElementTemp;
  },

  /**
   * Returns a hook to use Contentful's Inspector Mode specifying the entry once.
   *
   * @see https://www.contentful.com/developers/docs/tutorials/general/live-preview/#set-up-inspector-mode
   */
  useInspectorMode: (entry?: Entry<unknown>) => {
    if (!entry) {
      return undefined;
    }
    return {
      getProps: (fieldId: string) => {
        return ContentfulLivePreview.getProps({ entryId: entry.sys.id, fieldId });
      },
    };
  },
  /**
   * Reduce contentful json by removing unnecessary data
   *
   * @param {any} data - entry to check
   *
   * @returns {any}
   */
  jsonCleanup: (data: any): any => {
    if (Array.isArray(data)) {
      return data.map(contentfulUtils.jsonCleanup);
    } else if (typeof data === "object" && data !== null) {
      const newData: any = {};
      for (const key in data) {
        if (key === "sys") {
          if (data[key].contentType) {
            newData[key] = {
              id: data[key].id,
              type: data[key].type,
              contentType: { sys: { id: data[key].contentType.sys.id } },
            };
          } else {
            newData[key] = {
              id: data[key].id,
              type: data[key].type,
            };
          }
        } else if (key !== "metadata") {
          newData[key] = contentfulUtils.jsonCleanup(data[key]);
        }
      }
      return newData;
    }
    return data;
  },
};

export default contentfulUtils;
